import { useEffect, useState } from "react";
import api from "@/lib/api";

const CACHE_TTL_MS = 60_000; // re-fetch at most once per minute

let _cache = null;
let _cacheAt = 0;
const _listeners = new Set();

function _notify(data) {
  _cache = data;
  _cacheAt = Date.now();
  _listeners.forEach((fn) => fn(data));
}

/**
 * Returns { tier, features, limits, usage, loading, refresh }.
 *
 * tier    — "free" | "pro" | "elite" | "admin"
 * features — { signal_feed: true, ai_copilot: false, … }
 * limits  — { signals_per_day: 3, strategies: 3, … }
 * usage   — { signals_per_day: { used, limit, remaining, exhausted }, … }
 */
export function useTier() {
  const [state, setState] = useState(
    _cache ?? { tier: "free", features: {}, limits: {}, usage: null, loading: true }
  );

  useEffect(() => {
    const update = (data) => setState({ ...data, loading: false });
    _listeners.add(update);

    if (!_cache || Date.now() - _cacheAt > CACHE_TTL_MS) {
      Promise.all([
        api.get("/features"),
        api.get("/payments/usage").catch(() => ({ data: { usage: null } })),
      ]).then(([f, u]) => {
        _notify({
          tier:     f.data.tier || "free",
          features: f.data.features || {},
          limits:   f.data.limits || {},
          usage:    u.data.usage || null,
        });
      }).catch(() => {
        setState((s) => ({ ...s, loading: false }));
      });
    }

    return () => _listeners.delete(update);
  }, []);

  const refresh = () => {
    _cacheAt = 0;
    _cache = null;
    setState((s) => ({ ...s, loading: true }));
  };

  return { ...state, refresh };
}

export const TIER_ORDER = ["free", "pro", "elite", "admin"];

export function tierAtLeast(userTier, required) {
  const ui = TIER_ORDER.indexOf(userTier);
  const ri = TIER_ORDER.indexOf(required);
  return ui >= ri;
}
